import React from 'react';
import {UserAvatar, UserCheckAvatar, UserFirstPosition, CopyIcon} from 'src/ui-components/Iconpack';
import Mic from 'src/assets/icons/mic.svg';
import BackgroundPattern from 'src/assets/icons/background-pattern-decorative.svg';
import BackgroundMic from 'src/assets/icons/background-mic.svg';
import {Button} from 'src/ui-components/Button/button';
import {ReferralCodeInterface} from 'src/reducers/users';
import {toast} from 'react-toastify';
import {
    Tooltip,
    TooltipArrow,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from 'src/ui-components/Tooltip/tooltip';
import Tracker from 'src/lib/analytics/tracker';
import {EventNames} from 'src/lib/analytics/events';

const formatCode = (code: string) => `${code.substring(0, 2)}-${code.substring(2, 4)}-${code.substring(4, 6)}`;

function IconTwitter(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox='0 0 1024 1024' fill='currentColor' height='1em' width='1em' {...props}>
            <path d='M928 254.3c-30.6 13.2-63.9 22.7-98.2 26.4a170.1 170.1 0 0075-94 336.64 336.64 0 01-108.2 41.2A170.1 170.1 0 00672 174c-94.5 0-170.5 76.6-170.5 170.6 0 13.2 1.6 26.4 4.2 39.1-141.5-7.4-267.7-75-351.6-178.5a169.32 169.32 0 00-23.2 86.1c0 59.2 30.1 111.4 76 142.1a172 172 0 01-77.1-21.7v2.1c0 82.9 58.6 151.6 136.7 167.4a180.6 180.6 0 01-44.9 5.8c-11.1 0-21.6-1.1-32.2-2.6C211 652 273.9 701.1 348.8 702.7c-58.6 45.9-132 72.9-211.7 72.9-14.3 0-27.5-.5-41.2-2.1C171.5 822 261.2 850 357.8 850 671.4 850 843 590.2 843 364.7c0-7.4 0-14.8-.5-22.2 33.2-24.3 62.3-54.4 85.5-88.2z' />
        </svg>
    );
}

function IconLinkedin(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg viewBox='0 0 1024 1024' fill='currentColor' height='1em' width='1em' {...props}>
            <path d='M880 112H144c-17.7 0-32 14.3-32 32v736c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V144c0-17.7-14.3-32-32-32zM349.3 793.7H230.6V411.9h118.7v381.8zm-59.3-434a68.8 68.8 0 1168.8-68.8c-.1 38-30.9 68.8-68.8 68.8zm503.7 434H675.1V608c0-44.3-.8-101.2-61.7-101.2-61.7 0-71.2 48.2-71.2 98v188.9H423.7V411.9h113.8v52.2h1.6c15.8-30 54.5-61.7 112.3-61.7 120.2 0 142.3 79.1 142.3 181.9v209.4z' />
        </svg>
    );
}

function IconWhatsapp(props: React.SVGProps<SVGSVGElement>) {
    return (
        <svg fill='currentColor' viewBox='0 0 16 16' height='1em' width='1em' {...props}>
            <path d='M13.601 2.326A7.854 7.854 0 007.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 003.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0013.6 2.326zM7.994 14.521a6.573 6.573 0 01-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 01-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 014.66 1.931 6.557 6.557 0 011.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007a.729.729 0 00-.529.247c-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z' />
        </svg>
    );
}

export default function UserReferral({code, referralCount}: {code: ReferralCodeInterface; referralCount: number}) {
    const fancyCode = formatCode(code.code);
    const signupUrl = `${window.location.origin}/signup?c=${code.code}`;
    const copyText = (text: string) => navigator.clipboard.writeText(text);

    const shareText = [
        'Hey! I just signed up for PerceptPixel - An Image CDN with automatic optimization, real-time transformations and more!',
        '',
        `Use my referral code "${fancyCode}" on sign up to get 2GB extra bandwidth.`,
        '',
        signupUrl,
    ].join('\n');

    return (
        <div className='text-[1.125rem] font-[600] text-[#101828]'>
            <div className='flex pr-[1rem]'>
                <div>
                    <img src={BackgroundMic} alt='bgmic' className='absolute left-[16px] top-[16px]' />
                    <img src={BackgroundPattern} alt='bgpattern' className='absolute left-[-1px] top-[-1px]' />
                    <img src={Mic} alt='mic' className='scale-x-[-1]' />
                </div>
                <div className='relative flex flex-col'>
                    <span>Refer and get additional bandwidth</span>
                    <span className='pt-[0.25rem] text-[0.875rem] font-[400] text-[#475467]'>
                        Share the below code or send an invite to your friends
                    </span>
                </div>
            </div>
            <div className='relative py-[2.188rem]'>
                <div className='rounded-[0.75rem] bg-[#EFF4FF] py-[1.563rem] pl-[3rem]'>
                    <span className='text-[1rem] font-[700] text-[#000000]'>
                        Get 2 GB on referring a friend. Add 20 GB of free bandwidth.
                    </span>
                    <div className='flex justify-end pr-[5rem] pt-[2rem]'>
                        <span className='pr-2 text-[2rem] font-[700] text-[#0C111D]'>{fancyCode}</span>
                        <div
                            className='flex cursor-pointer pt-[0.25rem]'
                            onClick={() => {
                                copyText(fancyCode).then(() => {
                                    Tracker.trackEvent(EventNames.REFERRAL_CODE_COPIED);
                                    toast.success('Referral Code Copied!');
                                });
                            }}>
                            <CopyIcon width={25} height={25} color='#155EEF' />
                        </div>
                    </div>
                    <div className='relative pb-[1.25rem] pt-[43px]'>
                        <div className='h-[6px] w-[560px] bg-[#B3B8DB] pl-[5px]'></div>
                        <div className='absolute left-[-2px] top-[-10px] flex w-[565px] justify-between pt-[43px]'>
                            {Array.from({length: 10}, (_, index) => (
                                <div key={index} className='flex h-6 items-center'>
                                    {index < referralCount ? (
                                        <UserCheckAvatar />
                                    ) : index === referralCount ? (
                                        <TooltipProvider key={index}>
                                            <Tooltip>
                                                <TooltipTrigger>
                                                    <UserFirstPosition />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>You are here</p>
                                                    <TooltipArrow />
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ) : (
                                        <UserAvatar />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
            <div className='flex flex-col'>
                <div className={'flex'}>
                    <span className='text-[0.875rem] font-[500] text-[#344054]'>Referral Link</span>
                </div>
                <div className={'flex flex-row'}>
                    <input
                        type='text'
                        className='placegolder:text-[1rem] grow rounded-[8px] border border-[#D0D5DD] px-[14px] py-[10px] placeholder-[#667085] placeholder:font-[400]'
                        value={signupUrl}
                        disabled
                    />
                    <Button
                        size='lg'
                        className='ml-2'
                        onClick={() => {
                            copyText(signupUrl).then(() => {
                                Tracker.trackEvent(EventNames.REFERRAL_URL_COPIED);
                                toast.success('Referral Url Copied!');
                            });
                        }}>
                        <CopyIcon width={24} height={24} color='#FFFFFF' />
                        Copy URL
                    </Button>
                    <Button
                        size={'lg'}
                        className={'ml-2'}
                        onClick={() => {
                            Tracker.trackEvent(EventNames.TWITTER_SHARE_CLICKED);
                            window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`);
                        }}>
                        <IconTwitter />
                    </Button>
                    <Button
                        size={'lg'}
                        className={'ml-2'}
                        onClick={() => {
                            Tracker.trackEvent(EventNames.LINKEDIN_SHARE_CLICKED);
                            window.open(
                                `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareText)}`,
                            );
                        }}>
                        <IconLinkedin />
                    </Button>
                    <Button
                        size={'lg'}
                        className={'ml-2'}
                        onClick={() => {
                            Tracker.trackEvent(EventNames.WHATSAPP_SHARE_CLICKED);
                            window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`);
                        }}>
                        <IconWhatsapp />
                    </Button>
                </div>
            </div>
        </div>
    );
}
